<template>
  <b-container class="mt-3 mb-2">
    <validation-observer ref="mainForm" v-slot="{ invalid }">
      <b-card>
        <b-container class="m-0 p-0">
          <b-row>
            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <validation-provider :name="$tc('message.description', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 50 }"
                                   v-slot="{ valid, errors }">
                <b-form-group :label="$tc('message.description', 1)" label-for="input-descr">
                  <b-input-group>
                    <b-form-input id="input-descr"
                                  v-model="input.descr"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: G-0`"
                                  :state="itemChanged && !valid ? false : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <validation-provider name="email"
                                   ref="mailProvider"
                                   :rules="{ min: 0, max: 50, email: true }"
                                   v-slot="{ changed, valid, errors }">
                <b-form-group>
                  <label slot="label" class="m-0 p-0">
                    {{$t('message.add')}} {{$tc('message.new', 2).toLowerCase()}} email

                    (<font-awesome-icon icon="info-circle"
                                        class="c-green hover-enabled"
                                        @click="showInfoModal(`${$tc('info.needAtLeastOneEmail')}`)"/>)
                  </label>

                  <b-input-group>
                    <b-form-input id="new-mail"
                                  v-model="newMail"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: abcd@efg.com`"
                                  :state="changed ? valid && !alreadyExist : null">
                    </b-form-input>

                    <b-input-group-append class="br-tr br-br">
                      <b-button v-on:click="addMail(newMail)" class="br-tr br-br"
                                id="new-mail-button" :disabled="!newMail || !valid || alreadyExist"> +
                      </b-button>
                    </b-input-group-append>

                    <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>

                    <div v-if="alreadyExist" class="my-invalid-feedback">
                      <h6>{{$t('errors.unusual', 1)}}</h6>
                    </div>
                  </b-input-group>

                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row class="border-bottom border-c-green mb-3">
            <b-col>
              <font-awesome-icon icon="envelope"
                                 id="mail-icon"
                                 size="lg"
                                 :class="mailList && mailList.length !== 0 ? 'c-green' : 'text-danger'"/>
              <label class="c-green ml-1">
                {{$tc('message.list', 1)}} {{$tc('message.recipients', 2).toLowerCase()}}
              </label>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <b-list-group>
                <b-list-group-item v-for="mail in mailList" :key="mail">
                  <span class="float-left mt-1">{{mail}}</span>
                  <b-button class="float-right" v-on:click="disableMailCheck(mail)" size="md">
                    <font-awesome-icon icon="trash"/>
                  </b-button>
                </b-list-group-item>
              </b-list-group>
            </b-col>
          </b-row>

          <b-row class="mt-0">
            <b-col class="mt-3 justify-content-end d-flex">
              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2" v-on:click="save(false)"
                        id="save"
                        v-if="itemId ? config.u : config.c"
                        :disabled="checkSaveButton() || invalid">{{$t('message.save')}}
              </b-button>

              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2" v-on:click="save(true)"
                        id="save-and-close"
                        v-if="itemId ? config.u : config.c"
                        :disabled="checkSaveButton() || invalid">{{$t('message.saveClose')}}
              </b-button>

              <b-button class="b-c-green" v-on:click="pushBack()"
                        id="close">{{$t('message.close')}}</b-button>
            </b-col>
          </b-row>
        </b-container>
      </b-card>
    </validation-observer>

    <b-modal ref="deleteModal" id="deleteModal" :title="`${$t('message.caution')}!`">
      <b-container>
        <h5>{{$t('message.deleteCheck')}} {{mailToErase}}?</h5>
      </b-container>

      <div slot="modal-footer" class="w-100 justify-content-end d-flex">
        <b-button id="nw-delete-close" class="m-1 b-c-green" @click="hideDeleteModal()">{{$t('message.cancel')}}</b-button>

        <b-button id="nw-delete-continue" class="m-1 b-c-green" @click="deleteMail(mailToErase)">{{$t('message.continue')}}</b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import Vue from 'vue'
import { first, cloneDeep, find, remove } from 'lodash'

export default {
  name: 'EditRecipients',
  created () {
    this.checkWatcher()
    this.fetchData()
  },
  watch: {
    newMail () {
      this.alreadyExist = false
    }
  },
  data () {
    const itemId = this.$route.params.id !== 'new' ? this.$route.params.id : null
    const config = this.$route.meta.config

    return {
      config,
      first,
      itemId,
      input: {},
      mailList: [],
      newMail: '',
      itemChanged: false,
      mailToErase: null,
      alreadyExist: false
    }
  },
  methods: {
    checkWatcher () {
      this.$root.$emit('activeLoader', true)
      if (this.watcher) this.watcher()
      this.itemChanged = false
    },
    addWatcher () {
      this.watcher = this.$watch('input', () => {
        this.itemChanged = true
      }, { deep: true })
      this.$root.$emit('activeLoader', false)
    },
    fetchData () {
      if (this.itemId) {
        Vue.myGet(this, `/recipients_list/${this.itemId}`, 'recipients').then((recipients) => {
          const item = first(recipients)
          this.input = item
          this.mailList = cloneDeep(item.recipients)
          this.addWatcher()
        })
      } else {
        this.input = {}
        this.mailList = []
        this.addWatcher()
      }
    },
    addMail (mail) {
      const foundedMail = find(this.mailList, obj => obj === mail)
      if (!foundedMail) {
        this.mailList.push(mail)
        this.newMail = ''
        this.itemChanged = true
        this.alreadyExist = false
      } else {
        this.alreadyExist = true
      }
    },
    showInfoModal (message) {
      this.$root.$emit('info-modal', message)
    },
    disableMailCheck (mail) {
      this.mailToErase = mail
      this.$refs.deleteModal.show()
    },
    hideDeleteModal () {
      this.mailToErase = null
      this.$refs.deleteModal.hide()
    },
    deleteMail (mail) {
      this.mailList = remove(this.mailList, obj => obj !== mail)
      this.itemChanged = true
      this.hideDeleteModal()
    },
    checkSaveButton () {
      const changed = this.itemChanged
      const description = this.input.descr
      const hasMails = this.mailList && this.mailList.length !== 0

      return !changed || !description || !hasMails
    },
    pushBack (obj) {
      const backObj = obj || { name: 'Recipients' }
      if (this.itemChanged) this.$root.$emit('go-back-modal', backObj)
      else this.$router.push(backObj)
    },
    save (pushBack, abortRefresh) {
      const newItem = this.input
      newItem.recipients = this.mailList
      return Vue.putOrPost(this, !!newItem.id, `/recipients_list${newItem.id ? '/' + newItem.id : ''}`, newItem).then((r) => {
        Vue.success(this, `${this.$tc('message.recipients', 1)} ${this.$t(newItem.id ? 'success.putSuccess' : 'success.postSuccess')}`)
        if (pushBack) {
          this.itemChanged = false
          this.pushBack()
        } else if (!abortRefresh) {
          this.$router.push({ name: 'RefreshPage', params: { id: newItem.id || r.data.id, name: 'EditRecipients' } })
        }
        return true
      }).catch(() => {
        Vue.error(this, this.$t('errors.generic'))
        return false
      })
    }
  }
}
</script>

<style scoped>

</style>
